// Query Editor Map view (T13) — hosts the shared SpatialMap over a result set's FULL geometry values.
// Rows come from `useSpatialResultRows` (bounded full-value fetch — NEVER the 1 000-row chart sample);
// features from `useResultSetFeatures` (async decode, ≤1 KB properties, geometry excluded). Reused by
// the Table Explorer map pane (T14); onViewportChange/onMapClick are forwarded for T14/T15 — T13 itself ignores them.
//
// T28 — the J/K feature tour and Alt coordinate-probe copy live ENTIRELY inside `SpatialMap`/
// `SpatialMapLazy`; this panel only threads the EXISTING `onFeatureClick`/`selectedFeature` props
// through and derives `probeContext` from data it already computes — `handleFeatureClick` below is
// the ONE seam a mouse click and a tour "click" share.
// T31 — a replay control appears when the result carries a datetime column, picked from METADATA
// alone (`findCandidateTimeColumnId`, joined into `neededColumnIds` per A7/NG9); visibility applies
// at the map's SOURCE, never a layer filter (A7/NG10) — see `SpatialResultsMapCanvas`.
// T29 — the sanity-check nudge chip + axis-swap toggle ALSO live ENTIRELY inside `SpatialMap`
// (A16/NG8, so Spatial Explorer gets them too); `handleContainerMouseDown` below only stops this
// panel's own J/K-focus behavior from closing that chip's portalled popover (see its doc comment).

import { useMemo, useRef, useState, type ReactNode } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { QueryResultSet } from '../../../../types/queryExecution';
import { useAnalytics } from '../../../../core/analytics';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import type { SpatialMapViewport, SpatialProbeContext, SpatialDrawContract } from '../map/spatialMapTypes';
import { DEFAULT_SPATIAL_FEATURE_CAP } from '../types/spatialTypes';
import { useSpatialResultColumns } from './spatialResultColumns';
import { useSpatialResultRows, type WindowedRowSource } from './useSpatialResultRows';
import { useResultSetFeatures } from './useResultSetFeatures';
import { useSpatialResultsMapSelection } from './useSpatialResultsMapSelection';
import { SpatialResultsMapControls } from './SpatialResultsMapControls';
import { buildBannerParts, deriveHoverContent, resolveColorByPick, resolveGeometryPick } from './spatialResultsMapView';
import { useSpatialProbeContext } from './useSpatialProbeContext';
import { SpatialResultsMapBanner, SpatialResultsMapEmpty } from './SpatialResultsMapStatus';
import { useSpatialResultsMapHandlers, type Hover } from './useSpatialResultsMapHandlers';
import { useSpatialResultsMapReplay } from './useSpatialResultsMapReplay';
import { findCandidateTimeColumnId, type TemporalReplayColumn } from './useTemporalReplay';
import { SpatialResultsMapCanvas } from './SpatialResultsMapCanvas';

export type SpatialResultsMapPanelProps = {
  resultSet: QueryResultSet | undefined;
  engine: DatabaseEngineId;
  connectionId?: string;
  database?: string;
  onRowSelect?: (rowIndex: number) => void;
  selectedRowIndex?: number | null;
  onViewportChange?: (viewport: SpatialMapViewport) => void;
  onMapClick?: (lngLat: [number, number]) => void;
  /** T15 polygon-draw "in area" filter mode (TE map pane only; QE Map view leaves it undefined). */
  draw?: SpatialDrawContract | null;
  /** Distinct SRIDs of the decoded features (T15 — sources the filter chip's SRID). */
  onSridsChange?: (srids: number[]) => void;
  /** Present for windowed QE results (full-value fetch). Absent ⇒ inline rows already full. */
  windowed?: WindowedRowSource;
  /** Empty-overlay headline when the input window has ZERO rows (T14 TE pane). */
  emptyRowsLabel?: string;
  /** T28 — which host surface this panel is mounted in (`probeContext.surface`). Defaults to 'query'. */
  surface?: SpatialProbeContext['surface'];
  /**
   * TE-only Filter group (the "in view" + "draw area" buttons) rendered into the toolbar's Filter
   * slot. The QE Map view leaves it undefined, so no Filter group appears there.
   */
  filterControls?: ReactNode;
  /**
   * Seed the geometry / color-by pickers' INITIAL state (dashboards map tile — the persisted
   * `MapTile.geometryColumn` / `colorByColumn` preferences). Initial mount only: once the user
   * changes a picker, panel state wins; a named column that isn't a valid choice for the current
   * result falls back to today's auto-pick / None exactly (`resolveGeometryPick` /
   * `resolveColorByPick`). QE Map view / TE map pane simply leave these undefined.
   */
  initialGeometryColumn?: string;
  initialColorBy?: string;
};

export function SpatialResultsMapPanel(props: SpatialResultsMapPanelProps) {
  const { resultSet, engine, connectionId, database, onRowSelect, selectedRowIndex, windowed } = props;
  const { track } = useAnalytics();
  const cap = DEFAULT_SPATIAL_FEATURE_CAP;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const columns = useSpatialResultColumns(resultSet, engine, connectionId, database);
  const { spatialColumnIds, nonSpatialColumnIds, columnDataTypes, columnLabels, declaredSpatialColumns } = columns;

  const [geometryColumnId, setGeometryColumnId] = useState<string | null>(props.initialGeometryColumn ?? null);
  const [colorByColumnId, setColorByColumnId] = useState<string | null>(props.initialColorBy ?? null);
  const [hover, setHover] = useState<Hover>(null);

  const geomId = resolveGeometryPick(geometryColumnId, spatialColumnIds);
  const colorBy = resolveColorByPick(colorByColumnId, nonSpatialColumnIds);

  // A7/NG9 — the candidate timestamp column is picked from METADATA alone (no row sampling yet),
  // so it can be joined into `neededColumnIds` before the windowed fetch below ever runs.
  const replayColumns: TemporalReplayColumn[] = useMemo(
    () => nonSpatialColumnIds.map((id) => ({ id, dataType: columnDataTypes.get(id) })),
    [nonSpatialColumnIds, columnDataTypes],
  );
  const candidateTimeColumnId = useMemo(() => findCandidateTimeColumnId(replayColumns), [replayColumns]);

  const neededColumnIds = useMemo(() => {
    const ids = geomId ? [geomId, ...nonSpatialColumnIds] : [...nonSpatialColumnIds];
    if (candidateTimeColumnId) ids.push(candidateTimeColumnId);
    return Array.from(new Set(ids));
  }, [geomId, nonSpatialColumnIds, candidateTimeColumnId]);

  const rowsSource = useSpatialResultRows({ resultSet, neededColumnIds, cap, windowed });
  const replay = useSpatialResultsMapReplay(rowsSource.rows, replayColumns);

  const geometryRef = useMemo(
    () =>
      geomId
        ? {
            columnId: geomId,
            dataType: columnDataTypes.get(geomId),
            declaration: declaredSpatialColumns.get(geomId),
          }
        : null,
    [geomId, columnDataTypes, declaredSpatialColumns],
  );
  const featureState = useResultSetFeatures(rowsSource.rows, engine, geometryRef, cap, {
    attachColumnIds: nonSpatialColumnIds,
  });
  const decodedFeatures = featureState.features;

  const { probeContext } = useSpatialProbeContext({
    decodedFeatures,
    engine,
    geomId,
    isDeclared: Boolean(geometryRef?.declaration),
    dataType: geometryRef?.dataType,
    surface: props.surface ?? 'query',
    onSridsChange: props.onSridsChange,
  });

  const { layers, legend, featureById, selectedFeature, renderedCount } = useSpatialResultsMapSelection({
    decodedFeatures,
    cap,
    colorByColumnId: colorBy,
    selectedRowIndex,
    visibleKeys: replay.timeColumn ? replay.visibleKeys : null,
  });

  const { handleGeometryChange, handleColorByChange, handleFeatureClick, handleHover, handleKeyDown, handleContainerMouseDown } =
    useSpatialResultsMapHandlers({ track, setGeometryColumnId, setColorByColumnId, setHover, onRowSelect, containerRef });

  const loading = rowsSource.loading || featureState.loading;
  const decodedCount = featureState.features.length; // gates Empty vs Canvas — NOT replay-filtered
  const bannerParts = buildBannerParts({
    truncated: rowsSource.total > rowsSource.rowsReturned,
    rowsReturned: rowsSource.rowsReturned,
    total: rowsSource.total,
    byteBudgetExceeded: rowsSource.byteBudgetExceeded,
    tooLargeCount: featureState.tooLargeCount,
  });

  const hoverFeature = hover ? featureById.get(hover.id) : undefined;
  const hoverContent = deriveHoverContent(hoverFeature, nonSpatialColumnIds, columnLabels);

  const geometryOptions = spatialColumnIds.map((id) => ({ id, label: columnLabels.get(id) ?? id }));
  const colorByOptions = nonSpatialColumnIds.map((id) => ({ id, label: columnLabels.get(id) ?? id }));

  return (
    <div
      ref={containerRef}
      data-testid="spatial-results-map-panel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseDownCapture={handleContainerMouseDown}
      className="flex h-full min-h-0 w-full flex-col outline-none"
    >
      <div className="flex flex-shrink-0 items-center border-b border-border/60">
        <SpatialResultsMapControls
          geometryOptions={geometryOptions}
          geometryColumnId={geomId ?? ''}
          onGeometryChange={handleGeometryChange}
          colorByOptions={colorByOptions}
          colorByColumnId={colorBy}
          onColorByChange={handleColorByChange}
          filterControls={props.filterControls}
          featureCount={renderedCount}
        />
      </div>

      <SpatialResultsMapBanner parts={bannerParts} />

      <div className="relative min-h-0 flex-1">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center" data-testid="spatial-map-loading">
            <RandomDotmSpinner size={24} />
          </div>
        ) : decodedCount === 0 ? (
          <SpatialResultsMapEmpty
            nullCount={featureState.nullCount}
            failedCount={featureState.failedCount - featureState.tooLargeCount}
            tooLargeCount={featureState.tooLargeCount}
            title={props.emptyRowsLabel && rowsSource.total === 0 ? props.emptyRowsLabel : undefined}
          />
        ) : (
          <SpatialResultsMapCanvas
            layers={layers}
            onFeatureClick={handleFeatureClick}
            onFeatureHover={handleHover}
            selectedFeature={selectedFeature}
            onViewportChange={props.onViewportChange}
            onMapClick={props.onMapClick}
            probeContext={probeContext}
            draw={props.draw}
            legend={legend}
            hoverPoint={hover}
            hoverContent={hoverContent}
            replay={replay}
          />
        )}
      </div>
    </div>
  );
}
